import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queriesApi } from '../services/queriesService'

/**
 * Hook para obtener y procesar los datos de CareNetwork de Contentful
 * Utiliza Tanstack Query para cache de 5 días y mejores prácticas
 *
 * @returns {Object} Objeto con loading, error y datos procesados
 */
export const useContentfulCareNetwork = () => {
	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'carenetwork'] as const,
		queryFn: queriesApi.getCareNetwork,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Manejar errores de manera moderna
	if (error) {
		console.error('Error al obtener datos de CareNetwork desde Contentful:', error)
	}

	// Procesar datos usando memoización para evitar recalculos innecesarios
	const careNetState = useMemo(() => {
		if (!rawData || isLoading || error) {
			return {
				isCareNetData: false,
				isCareNeedsData: false,
				isCareTrainingData: false
			}
		}

		try {
			const data = rawData?.homeCollection?.items?.[0]?.vistas?.redDeCuidado
			return {
				isCareNetData: data || false,
				isCareNeedsData: data?.beneficioCollection?.items?.[0]?.beneficioCollection?.items || false,
				isCareTrainingData: data?.beneficioCollection?.items?.[1] || false
			}
		} catch (transformError) {
			console.error('Error al transformar datos de CareNetwork:', transformError)
			return {
				isCareNetData: false,
				isCareNeedsData: false,
				isCareTrainingData: false
			}
		}
	}, [rawData, isLoading, error])

	// Información adicional sobre el estado del cache
	const cacheInfo = useMemo(
		() => ({
			isStale,
			isRefetching,
			lastFetch: rawData ? new Date() : null,
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000) // 5 días desde ahora
		}),
		[isStale, isRefetching, rawData]
	)

	return {
		// Datos principales (manteniendo compatibilidad con el hook original)
		loading: isLoading,
		error,
		...careNetState,

		// Funcionalidades adicionales de Tanstack Query
		refetch,
		isStale,
		isRefetching,
		cacheInfo,

		// Datos raw por si se necesitan
		rawData
	}
}

export default useContentfulCareNetwork
